import React, { useState } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Chip,
  IconButton,
  Tab,
  Tabs,
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Code as CodeIcon,
} from '@mui/icons-material';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import config from '../config';

const APIReference = () => {
  const [copiedEndpoint, setCopiedEndpoint] = useState(null);
  const [tabValue, setTabValue] = useState(0);

  const token = localStorage.getItem('telegram_token');

  const endpoints = [
    {
      method: 'POST',
      path: '/login',
      description: 'Initiate phone login',
      parameters: [
        { name: 'phone_number', type: 'string', required: true, description: 'Phone number with country code' },
      ],
      example: `curl -X POST ${config.API_BASE}/login \\\n  -H "Content-Type: application/json" \\\n  -d '{"phone_number": "+855967171785"}'`,
      response: '{"detail": "Code sent"}',
    },
    {
      method: 'POST',
      path: '/verify',
      description: 'Verify login code',
      parameters: [
        { name: 'phone', type: 'string', required: true },
        { name: 'code', type: 'string', required: true },
        { name: 'phone_code_hash', type: 'string', required: true },
        { name: 'password', type: 'string', required: false },
      ],
      example: `curl -X POST ${config.API_BASE}/verify \\\n  -H "Content-Type: application/json" \\\n  -d '{\n    "phone": "+855967171785",\n    "code": "12345",\n    "phone_code_hash": "ABCD1234EFGH5678"\n  }'`,
      response: '{"access_token": "your_jwt_token", "token_type": "bearer"}',
    },
    {
      method: 'GET',
      path: '/telegram/status',
      description: 'Check Telegram connection status',
      auth: true,
      example: `curl -X GET ${config.API_BASE}/telegram/status \\\n  -H "Authorization: Bearer ${token || 'YOUR_TOKEN'}"`,
      response: '{"connected": true, "user": {...}}',
    },
    {
      method: 'GET',
      path: '/list-contacts',
      description: 'Get all contacts',
      auth: true,
      example: `curl -X GET ${config.API_BASE}/list-contacts \\\n  -H "Authorization: Bearer ${token || 'YOUR_TOKEN'}"`,
      response: '[{"id": "123", "first_name": "John", ...}]',
    },
    {
      method: 'GET',
      path: '/list-chats',
      description: 'Get all chats',
      auth: true,
      example: `curl -X GET ${config.API_BASE}/list-chats \\\n  -H "Authorization: Bearer ${token || 'YOUR_TOKEN'}"`,
      response: '[{"id": "456", "title": "Group Chat", ...}]',
    },
    {
      method: 'POST',
      path: '/chats/message/send',
      description: 'Send message to chat',
      auth: true,
      parameters: [
        { name: 'chat_id', type: 'integer', required: true },
        { name: 'message', type: 'string', required: true },
      ],
      example: `curl -X POST ${config.API_BASE}/chats/message/send \\\n  -H "Authorization: Bearer ${token || 'YOUR_TOKEN'}" \\\n  -H "Content-Type: application/json" \\\n  -d '{"chat_id": -4586738257, "message": "Hello!"}'`,
      response: '{"success": true, "message_id": 123}',
    },
  ];

  const handleCopy = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedEndpoint(index);
    setTimeout(() => setCopiedEndpoint(null), 2000);
  };

  const languageExamples = {
    python: `import requests

# Authentication
response = requests.post('${config.API_BASE}/login', json={
    'phone_number': '+855967171785'
})

# Send message
headers = {'Authorization': 'Bearer YOUR_TOKEN'}
response = requests.post('${config.API_BASE}/chats/message/send',
    headers=headers,
    json={
        'chat_id': -4586738257,
        'message': 'Hello from Python!'
    }
)`,
    
    javascript: `// Using fetch API
async function sendMessage() {
  const response = await fetch('${config.API_BASE}/chats/message/send', {
    method: 'POST',
    headers: {
      'Authorization': 'Bearer YOUR_TOKEN',
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      chat_id: -4586738257,
      message: 'Hello from JavaScript!'
    })
  });
  
  const data = await response.json();
  console.log(data);
}`,
    
    php: `<?php
// Send message with cURL
$ch = curl_init('${config.API_BASE}/chats/message/send');
curl_setopt($ch, CURLOPT_RETURNTRANSFER, true);
curl_setopt($ch, CURLOPT_POST, true);
curl_setopt($ch, CURLOPT_HTTPHEADER, [
    'Authorization: Bearer YOUR_TOKEN',
    'Content-Type: application/json'
]);
curl_setopt($ch, CURLOPT_POSTFIELDS, json_encode([
    'chat_id' => -4586738257,
    'message' => 'Hello from PHP!'
]));

$response = curl_exec($ch);
curl_close($ch);
echo $response;
?>`,
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ fontWeight: 700, mb: 4 }}>
        API Reference
      </Typography>

      <Card sx={{ borderRadius: 3, mb: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
            Base URL
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
            <Chip label="Base URL" color="primary" />
            <Typography sx={{ fontFamily: 'monospace', fontSize: '1.1rem' }}>
              {config.API_BASE}
            </Typography>
            <IconButton
              size="small"
              onClick={() => handleCopy(config.API_BASE, 'base')}
            >
              {copiedEndpoint === 'base' ? <CheckIcon /> : <CopyIcon />}
            </IconButton>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Authentication
          </Typography>
          <Box sx={{ mb: 4, p: 3, bgcolor: 'rgba(59, 130, 246, 0.1)', borderRadius: 2 }}>
            <Typography sx={{ mb: 2 }}>
              Most endpoints require Bearer Token authentication. Include the token in the Authorization header:
            </Typography>
            <SyntaxHighlighter language="bash" style={vscDarkPlus}>
              {`Authorization: Bearer ${token || 'YOUR_JWT_TOKEN'}`}
            </SyntaxHighlighter>
          </Box>

          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            API Endpoints
          </Typography>
          
          {endpoints.map((endpoint, index) => (
            <Accordion key={index} sx={{ mb: 2, bgcolor: 'rgba(255, 255, 255, 0.05)' }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                  <Chip
                    label={endpoint.method}
                    color={endpoint.method === 'GET' ? 'success' : 
                           endpoint.method === 'POST' ? 'primary' : 'warning'}
                    size="small"
                  />
                  <Typography sx={{ fontWeight: 600, flex: 1 }}>
                    {endpoint.path}
                  </Typography>
                  <Typography color="textSecondary">
                    {endpoint.description}
                  </Typography>
                  {endpoint.auth && (
                    <Chip label="Auth Required" color="secondary" size="small" />
                  )}
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                {endpoint.parameters && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                      Parameters:
                    </Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: 2 }}>
                      {endpoint.parameters.map((param, idx) => (
                        <Box key={idx} sx={{ p: 2, bgcolor: 'rgba(255, 255, 255, 0.05)', borderRadius: 1 }}>
                          <Typography variant="body2" sx={{ fontWeight: 600 }}>
                            {param.name}
                            <Chip
                              label={param.required ? 'Required' : 'Optional'}
                              size="small"
                              sx={{ ml: 1, height: 20 }}
                              color={param.required ? 'error' : 'default'}
                            />
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Type: {param.type}
                          </Typography>
                          {param.description && (
                            <Typography variant="body2" sx={{ mt: 0.5 }}>
                              {param.description}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}

                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
                  Example Request:
                </Typography>
                <Box sx={{ position: 'relative' }}>
                  <SyntaxHighlighter language="bash" style={vscDarkPlus}>
                    {endpoint.example}
                  </SyntaxHighlighter>
                  <IconButton
                    size="small"
                    onClick={() => handleCopy(endpoint.example, index)}
                    sx={{ position: 'absolute', top: 8, right: 8 }}
                  >
                    {copiedEndpoint === index ? <CheckIcon /> : <CopyIcon />}
                  </IconButton>
                </Box>

                {endpoint.response && (
                  <>
                    <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1, mt: 2 }}>
                      Example Response:
                    </Typography>
                    <SyntaxHighlighter language="json" style={vscDarkPlus}>
                      {endpoint.response}
                    </SyntaxHighlighter>
                  </>
                )}
              </AccordionDetails>
            </Accordion>
          ))}
        </CardContent>
      </Card>

      <Card sx={{ borderRadius: 3, bgcolor: 'background.paper' }}>
        <CardContent>
          <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
            Code Examples
          </Typography>
          
          <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)} sx={{ mb: 3 }}>
            <Tab label="Python" />
            <Tab label="JavaScript" />
            <Tab label="PHP" />
          </Tabs>

          <SyntaxHighlighter language={tabValue === 0 ? 'python' : tabValue === 1 ? 'javascript' : 'php'} style={vscDarkPlus}>
            {languageExamples[tabValue === 0 ? 'python' : tabValue === 1 ? 'javascript' : 'php']}
          </SyntaxHighlighter>

          <Box sx={{ mt: 3, p: 3, bgcolor: 'rgba(16, 185, 129, 0.1)', borderRadius: 2 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
              💡 Pro Tips:
            </Typography>
            <ul style={{ margin: 0, paddingLeft: '20px' }}>
              <li>Always handle errors and timeouts in your implementation</li>
              <li>Store tokens securely and refresh when needed</li>
              <li>Implement rate limiting on your client side</li>
              <li>Use async operations for better performance</li>
              <li>Test with different chat types (private, group, channel)</li>
            </ul>
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
};

export default APIReference;